// src/Pages/ValmikiAbout.jsx
// VALMIKI AI LAB — About page (Page 2 of the 6-page Phase 1 architecture)
// ONE self-contained file: Navbar + Footer + all CSS live here.
// Deliberately different from the Home page: light typographic hero, editorial sections,
// a rising "ascent" motif (hero line → journey steps), and a split Vision / Mission panel.
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).
import ValmikiFooter from "./ValmikiFooter";
import ValmikiLeadForm from "./ValmikiLeadPopup";
import React, { useEffect, useRef, useState } from "react";

/* ───────────────────────── CONFIG ───────────────────────── */

// Image direction: authentic faculty/student collaboration and modern learning environments.
// Drop in real photos (import heroPhoto from "../images/valmiki-about-hero.jpg") to show them.
// HERO_IMAGE replaces the animated line graphic in the hero strip.
// ENVIRONMENT_IMAGE adds a wide photo band before Vision / Mission.
const HERO_IMAGE = "";
const HERO_IMAGE_ALT = "Faculty and students collaborating on an AI project at VALMIKI AI LAB";
const ENVIRONMENT_IMAGE = "";
const ENVIRONMENT_IMAGE_ALT = "A modern VALMIKI AI LAB learning environment";

/* ───────────────────────── CONTENT ───────────────────────── */

const HEADLINE = "Building AI Capability for the Future of Work.";

const pillars = [
  "Learning",
  "Practice",
  "Implementation",
  "Real evidence of work",
];

const journey = [
  "Learn",
  "Practice",
  "Build",
  "Project",
  "Portfolio",
  "Internship",
  "Interview Readiness",
  "Career Growth",
];

const mission = [
  "Deliver practical and industry-oriented AI learning",
  "Develop portfolios through real implementation",
  "Connect learning with internship and career readiness",
  "Build strong academic and industry partnerships",
];

// Hero graphic: a stepped line that rises left → right.
const ASCENT_PATH = "M0 360 H150 V310 H300 V255 H450 V200 H600 V150 H750 V100 H900 V60 H1050";
const ASCENT_GRID_Y = [60, 120, 180, 240, 300, 360];
const ASCENT_GRID_X = [150, 300, 450, 600, 750, 900];

/* ───────────────────────── STYLES (in-page) ───────────────────────── */

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vl-root{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; margin:0;
}
.vl-root *{box-sizing:border-box;}
:where(.vl-root) :where(h1,h2,h3,p,ul,ol,figure){margin:0;padding:0;}
:where(.vl-root) :where(ul,ol){list-style:none;}
:where(.vl-root) a{color:inherit;}
.vl-root :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}

.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
.vl-section{padding:104px 0;}
.vl-dark{background:var(--ink);color:var(--on-ink);}
.vl-white{background:var(--white);}

.vl-h2{font-family:var(--serif);font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.12;letter-spacing:-.015em;}
.vl-lead{font-size:1.125rem;line-height:1.65;max-width:56ch;}

/* buttons — border and running lines share ONE colour.
   Two glowing lines chase each other around the border (clockwise), always on opposite sides.
   The gold buttons also "breathe": the fill brightens and a soft glow pulses.
   Hover: background colour wipes across, left → right.
   (The orbiting lines use @property; browsers without it show the lines static.) */
@property --vl-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vl-actions{display:flex;flex-wrap:wrap;gap:14px;}
.vl-btn{
  --fill:#162B47; --edge:var(--gold); --line:var(--gold);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before{
  content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vl-a),
    transparent 0deg,var(--line) 55deg,transparent 56deg,
    transparent 180deg,var(--line) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;
  animation:vl-orbit 3.2s linear infinite;
}
@keyframes vl-orbit{to{--vl-a:360deg;}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}

.vl-btn-gold{animation:vl-breathe 4s ease-in-out infinite;}
@keyframes vl-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}

.vl-btn-line-light{--fill:transparent;--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);--hover-text:var(--ink);}
.vl-btn-ink{--fill:var(--ink);--edge:var(--gold);--line:var(--gold);--wipe:var(--gold);--hover-text:var(--ink);--text:var(--paper);}
.vl-btn-line-ink{--fill:transparent;--edge:var(--ink);--line:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle{transition:none;}
  .vl-btn::before{animation:none;}
  .vl-btn-gold{animation:none;}
}

/* header */
.vl-header{position:sticky;top:0;z-index:50;background:var(--ink);border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:68px;gap:24px;}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
.vl-nav{display:flex;align-items:center;gap:26px;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 20px;font-size:12.5px;letter-spacing:.09em;}
.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:var(--ink);border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}
.vl-mobile .vl-btn{margin-top:18px;width:100%;}
@media (max-width:1000px){
  .vl-nav{display:none;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
}

/* ─────────── HERO: light, typographic, one wide strip ─────────── */
.vl-hero{background:var(--paper);padding:96px 0 96px;overflow:hidden;}
.vl-hero-h1{font-family:var(--serif);font-weight:500;font-size:clamp(2.7rem,7.4vw,6rem);
  line-height:1;letter-spacing:-.035em;max-width:12em;}
.vl-word{display:inline-block;overflow:hidden;vertical-align:top;padding:.06em 0 .14em;margin:-.06em 0 -.14em;}
.vl-word > span{display:inline-block;}
.vl-hero-rule{height:1px;background:var(--ink);opacity:.28;margin:56px 0 0;transform-origin:left center;}
.vl-hero-row{display:grid;grid-template-columns:5fr 7fr;gap:64px;padding:40px 0 72px;}
.vl-hero-copy{grid-column:2;}
.vl-hero-copy .vl-lead{margin-bottom:32px;}

.vl-strip{position:relative;overflow:hidden;border-radius:10px;background:var(--ink);aspect-ratio:21/8.5;}
.vl-strip img{display:block;width:100%;height:100%;object-fit:cover;}
.vl-strip svg{position:absolute;inset:0;width:100%;height:100%;}
.vl-asc-grid line{stroke:rgba(243,245,248,.07);stroke-width:1;}
.vl-asc-area{fill:url(#vl-asc-fill);}
.vl-asc-line{fill:none;stroke:var(--gold);stroke-width:3;stroke-linejoin:round;stroke-dasharray:1;stroke-dashoffset:0;}
.vl-asc-node{fill:var(--gold-hi);}
.vl-asc-ring{fill:none;stroke:var(--gold-hi);stroke-width:1.5;opacity:0;transform-box:fill-box;transform-origin:center;}

/* hero motion: one quick sequence — words rise, rule draws, copy + strip arrive, line traces upward */
@media (prefers-reduced-motion:no-preference){
  .vl-word > span{transform:translateY(112%);animation:vl-word .55s cubic-bezier(.2,.7,.2,1) forwards;
    animation-delay:calc(.05s + var(--i) * .045s);}
  .vl-hero-rule{transform:scaleX(0);animation:vl-scale-x .7s cubic-bezier(.65,0,.35,1) .5s forwards;}
  .vl-hero-copy{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) .55s both;}
  .vl-strip{animation:vl-rise .65s cubic-bezier(.2,.7,.2,1) .7s both;}
  .vl-asc-line{stroke-dashoffset:1;animation:vl-trace 1.4s cubic-bezier(.65,0,.35,1) .9s forwards;}
  .vl-asc-area{opacity:0;animation:vl-fade-in .8s ease 1.8s forwards;}
  .vl-asc-node{opacity:0;animation:vl-fade-in .3s ease 2.2s forwards;}
  .vl-asc-ring{animation:vl-ring 2.4s ease-out 2.6s infinite;}
  .vl-hero .vl-btn::before{animation-duration:2s;}
  .vl-hero .vl-btn-gold{animation-duration:2.5s;}
}
@keyframes vl-word{to{transform:none;}}
@keyframes vl-scale-x{to{transform:scaleX(1);}}
@keyframes vl-rise{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:none;}}
@keyframes vl-trace{to{stroke-dashoffset:0;}}
@keyframes vl-fade-in{to{opacity:1;}}
@keyframes vl-ring{0%{opacity:.8;transform:scale(1);}100%{opacity:0;transform:scale(3.4);}}

/* scroll reveal: children marked .vl-r fade up one after another when the block enters view */
@media (prefers-reduced-motion:no-preference){
  .vl-reveal .vl-r{opacity:0;transform:translateY(16px);
    transition:opacity .55s ease,transform .55s cubic-bezier(.2,.7,.2,1);
    transition-delay:calc(var(--i,0) * .08s);}
  .vl-reveal.in .vl-r{opacity:1;transform:none;}
}

/* ─────────── WHY VALMIKI EXISTS: editorial, with a horizontal bridge ─────────── */
.vl-why{display:grid;grid-template-columns:5fr 7fr;gap:72px;align-items:start;}
.vl-big{font-family:var(--serif);font-size:clamp(1.5rem,2.6vw,2.125rem);line-height:1.3;letter-spacing:-.01em;}
.vl-big + .vl-lead{margin-top:28px;}
.vl-bridge{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:28px;
  margin-top:80px;padding-top:44px;border-top:1px solid var(--line);}
.vl-bridge-end{font-family:var(--serif);font-size:clamp(1.25rem,2vw,1.625rem);line-height:1.2;max-width:11em;}
.vl-bridge-end:last-child{text-align:right;}
.vl-bridge-mid{position:relative;height:3px;min-width:80px;}
.vl-bridge-mid::before,.vl-bridge-mid::after{content:"";position:absolute;top:-8px;width:3px;height:19px;background:var(--ink);}
.vl-bridge-mid::before{left:0;}
.vl-bridge-mid::after{right:0;}
.vl-bridge-line{position:absolute;inset:0;background:var(--gold);transform-origin:left center;}
.vl-bridge-tag{position:absolute;left:50%;top:-30px;transform:translateX(-50%);
  font:600 11.5px/1 var(--mono);letter-spacing:.1em;color:var(--gold-deep);}
@media (prefers-reduced-motion:no-preference){
  .vl-bridge-line{transform:scaleX(0);transition:transform 1s cubic-bezier(.65,0,.35,1) .4s;}
  .vl-reveal.in .vl-bridge-line{transform:scaleX(1);}
}

/* ─────────── PHILOSOPHY ─────────── */
.vl-philo-statement{font-family:var(--serif);font-weight:500;font-size:clamp(2.1rem,5.4vw,4.25rem);
  line-height:1.05;letter-spacing:-.025em;max-width:14em;margin:36px 0 28px;}
.vl-philo .vl-lead{color:var(--on-ink-mute);}
.vl-pillars{display:grid;grid-template-columns:repeat(4,1fr);margin-top:72px;}
.vl-pillars li{position:relative;padding:22px 24px 0 0;margin-right:24px;
  font-family:var(--serif);font-size:1.5rem;line-height:1.25;}
.vl-pillars li::before{content:"";position:absolute;top:0;left:0;right:0;height:2px;background:var(--gold);
  transform-origin:left center;}
@media (prefers-reduced-motion:no-preference){
  .vl-pillars li::before{transform:scaleX(0);transition:transform .8s cubic-bezier(.65,0,.35,1);
    transition-delay:calc(.3s + var(--i,0) * .12s);}
  .vl-reveal.in .vl-pillars li::before{transform:scaleX(1);}
}

/* ─────────── LEARNING JOURNEY: rising steps ─────────── */
.vl-journey-head{max-width:40rem;margin-bottom:56px;}
.vl-steps{display:flex;align-items:flex-end;gap:6px;height:340px;}
.vl-step{flex:1;height:var(--h);position:relative;background:var(--white);border-top:3px solid var(--gold);
  padding:16px 12px 14px;display:flex;flex-direction:column;justify-content:space-between;transform-origin:bottom center;}
.vl-step-n{font:400 12.5px/1 var(--mono);color:var(--slate);}
.vl-step-t{font-family:var(--serif);font-size:1.125rem;line-height:1.2;}
.vl-step:last-child{background:var(--ink);color:var(--paper);}
.vl-step:last-child .vl-step-n{color:var(--gold-hi);}
@media (prefers-reduced-motion:no-preference){
  .vl-reveal .vl-step{transform:scaleY(0);transition:transform .8s cubic-bezier(.2,.7,.2,1);
    transition-delay:calc(var(--i,0) * .1s);}
  .vl-reveal.in .vl-step{transform:none;}
}

/* optional environment photo */
.vl-band{padding:0 0 104px;}
.vl-band img{display:block;width:100%;aspect-ratio:21/9;object-fit:cover;border-radius:10px;}

/* ─────────── VISION + MISSION: two full-bleed panels ─────────── */
.vl-vm{display:grid;grid-template-columns:1fr 1fr;}
.vl-vm-panel{padding:104px 56px;}
.vl-vm-vision{background:var(--gold);color:var(--ink);
  padding-left:max(24px,calc((100vw - 1120px) / 2 + 24px));}
.vl-vm-mission{background:var(--ink);color:var(--on-ink);
  padding-right:max(24px,calc((100vw - 1120px) / 2 + 24px));}
.vl-vm-panel .vl-h2{margin-bottom:28px;}
.vl-vision{font-family:var(--serif);font-size:clamp(1.5rem,2.4vw,2rem);line-height:1.3;letter-spacing:-.01em;max-width:22em;}
.vl-mission li{position:relative;padding:18px 0 18px 30px;border-bottom:1px solid var(--line-ink);
  font-family:var(--serif);font-size:1.25rem;line-height:1.3;}
.vl-mission li:first-child{border-top:1px solid var(--line-ink);}
.vl-mission li::before{content:"";position:absolute;left:2px;top:27px;width:9px;height:9px;background:var(--gold);
  transform:rotate(45deg);}

/* closing */
.vl-close-row{display:grid;grid-template-columns:1fr auto;gap:48px;align-items:end;}
.vl-close-row .vl-h2{font-size:clamp(2.1rem,4.6vw,3.6rem);max-width:12em;}

/* footer */
.vl-footer{background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

/* responsive */
@media (max-width:1000px){
  .vl-pillars{grid-template-columns:repeat(2,1fr);row-gap:32px;}
}
@media (max-width:860px){
  .vl-section{padding:72px 0;}
  .vl-band{padding-bottom:72px;}
  .vl-hero{padding:56px 0 64px;}
  .vl-hero-rule{margin-top:40px;}
  .vl-hero-row{grid-template-columns:1fr;gap:0;padding:32px 0 48px;}
  .vl-hero-copy{grid-column:1;}
  .vl-strip{aspect-ratio:16/10;}
  .vl-why{grid-template-columns:1fr;gap:32px;}
  .vl-bridge{grid-template-columns:1fr;justify-items:start;gap:20px;margin-top:56px;}
  .vl-bridge-end:last-child{text-align:left;}
  .vl-bridge-mid{width:3px;height:72px;min-width:0;margin-left:8px;}
  .vl-bridge-mid::before,.vl-bridge-mid::after{left:-8px;width:19px;height:3px;}
  .vl-bridge-mid::before{top:0;}
  .vl-bridge-mid::after{top:auto;bottom:0;right:auto;}
  .vl-bridge-line{transform-origin:center top;}
  .vl-bridge-tag{left:22px;top:50%;transform:translateY(-50%);}
  .vl-band img{aspect-ratio:16/9;}
  .vl-vm{grid-template-columns:1fr;}
  .vl-vm-panel,.vl-vm-vision,.vl-vm-mission{padding:72px 24px;}
  .vl-close-row{grid-template-columns:1fr;gap:32px;align-items:start;}
  /* steps become horizontal bars that grow longer */
  .vl-steps{flex-direction:column;align-items:flex-start;height:auto;gap:8px;}
  .vl-step{flex:none;width:var(--w);height:auto;min-height:64px;flex-direction:row;align-items:center;justify-content:flex-start;
    gap:14px;border-top:0;border-left:3px solid var(--gold);transform-origin:left center;}
}
@media (prefers-reduced-motion:no-preference){
  @media (max-width:860px){
    .vl-bridge-line{transform:scaleY(0);}
    .vl-reveal.in .vl-bridge-line{transform:scaleY(1);}
    .vl-reveal .vl-step{transform:scaleX(0);}
    .vl-reveal.in .vl-step{transform:none;}
  }
}
@media (max-width:560px){
  .vl-wrap{padding:0 20px;}
  .vl-vm-panel,.vl-vm-vision,.vl-vm-mission{padding:64px 20px;}
  .vl-actions .vl-btn{width:100%;}
  .vl-pillars{grid-template-columns:1fr;}
  .vl-step{width:100%;}
}
`;

/* ───────────────────────── SMALL PIECES ───────────────────────── */

// Adds the class "in" once the block scrolls into view; children marked .vl-r (and the step / bridge /
// pillar pieces) then animate in. Without IntersectionObserver everything is simply shown.
function Reveal({ as: Tag = "div", className = "", children, ...rest }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag ref={ref} className={`vl-reveal${seen ? " in" : ""} ${className}`.trim()} {...rest}>
      {children}
    </Tag>
  );
}

// The hero strip graphic used when no HERO_IMAGE is set: a stepped line climbing left → right.
function AscentGraphic() {
  return (
    <svg viewBox="0 0 1050 420" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="vl-asc-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#C8A24A" stopOpacity=".28" />
          <stop offset="1" stopColor="#C8A24A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="vl-asc-grid">
        {ASCENT_GRID_Y.map((y) => <line key={`y${y}`} x1="0" y1={y} x2="1050" y2={y} />)}
        {ASCENT_GRID_X.map((x) => <line key={`x${x}`} x1={x} y1="0" x2={x} y2="420" />)}
      </g>
      <path className="vl-asc-area" d={`${ASCENT_PATH} V420 H0 Z`} />
      <path className="vl-asc-line" d={ASCENT_PATH} pathLength="1" />
      <circle className="vl-asc-ring" cx="975" cy="60" r="7" />
      <circle className="vl-asc-node" cx="975" cy="60" r="7" />
    </svg>
  );
}

/* ───────────────────── NAVBAR + FOOTER (inline) ───────────────────── */
// Update the paths below to match your router.

const VALMIKI_ROUTES = {
  home: "/valmiki",
  about: "/valmiki/about",
  program: "/valmiki/program",
  lab: "/valmiki/lab",
  partner: "/valmiki/partner",
  contact: "/valmiki/contact",
};

// `current: true` marks the active page — here it is ABOUT VALMIKI.
const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about, current: true },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact },
];

const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

/* ───────────────────────── PAGE ───────────────────────── */

export default function ValmikiAbout() {
  const words = HEADLINE.split(" ");

  return (
    <div className="vl-root" id="top">
      <style>{css}</style>

      <main>
        {/* HERO — light, typographic; words rise, a rule draws, then a wide strip with a rising line */}
        <section className="vl-hero" aria-labelledby="vl-about-title">
          <div className="vl-wrap">
            <h1 className="vl-hero-h1" id="vl-about-title" aria-label={HEADLINE}>
              {words.map((w, i) => (
                <React.Fragment key={`${w}-${i}`}>
                  <span className="vl-word" aria-hidden="true">
                    <span style={{ "--i": i }}>{w}</span>
                  </span>
                  {i < words.length - 1 ? " " : null}
                </React.Fragment>
              ))}
            </h1>

            <div className="vl-hero-rule" aria-hidden="true" />

            <div className="vl-hero-row">
              <div className="vl-hero-copy">
                <p className="vl-lead">
                  VALMIKI AI LAB is an applied AI learning, innovation and career development ecosystem
                  focused on practical capabilities for an AI-enabled world.
                </p>
                <div className="vl-actions">
                  <a className="vl-btn vl-btn-gold" href={VALMIKI_ROUTES.program}>Explore the Program</a>
                  <a className="vl-btn vl-btn-line-ink" href={VALMIKI_ROUTES.partner}>Partner With Us</a>
                </div>
              </div>
            </div>

            <div className="vl-strip">
              {HERO_IMAGE ? <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} /> : <AscentGraphic />}
            </div>
          </div>
        </section>

        {/* WHY VALMIKI EXISTS */}
        <section className="vl-section vl-white" aria-labelledby="vl-why">
          <Reveal className="vl-wrap">
            <div className="vl-why">
              <h2 className="vl-h2 vl-r" id="vl-why" style={{ "--i": 0 }}>Why VALMIKI exists</h2>
              <div>
                <p className="vl-big vl-r" style={{ "--i": 1 }}>
                  There is a growing gap between academic learning and the capabilities organizations
                  increasingly expect from emerging talent.
                </p>
                <p className="vl-lead vl-r" style={{ "--i": 2 }}>
                  VALMIKI exists to help bridge education and practical AI readiness.
                </p>
              </div>
            </div>

            <div
              className="vl-bridge vl-r"
              style={{ "--i": 3 }}
              role="img"
              aria-label="VALMIKI bridges academic learning and the capabilities organizations expect"
            >
              <span className="vl-bridge-end">Academic learning</span>
              <span className="vl-bridge-mid" aria-hidden="true">
                <span className="vl-bridge-line" />
                <span className="vl-bridge-tag">VALMIKI</span>
              </span>
              <span className="vl-bridge-end">Capabilities organizations expect</span>
            </div>
          </Reveal>
        </section>

        {/* OUR PHILOSOPHY */}
        <section className="vl-section vl-dark vl-philo" aria-labelledby="vl-philo">
          <Reveal className="vl-wrap">
            <h2 className="vl-h2 vl-r" id="vl-philo" style={{ "--i": 0 }}>Our philosophy</h2>
            <p className="vl-philo-statement vl-r" style={{ "--i": 1 }}>
              We don&apos;t teach AI only for certificates.
            </p>
            <p className="vl-lead vl-r" style={{ "--i": 2 }}>
              We focus on building capability through learning, practice, implementation and real
              evidence of work.
            </p>
            <ul className="vl-pillars" aria-label="How capability is built">
              {pillars.map((p, i) => (
                <li key={p} className="vl-r" style={{ "--i": i + 3 }}>{p}</li>
              ))}
            </ul>
          </Reveal>
        </section>

        {/* LEARNING JOURNEY — steps rise from Learn to Career Growth */}
        <section className="vl-section" aria-labelledby="vl-journey">
          <div className="vl-wrap">
            <div className="vl-journey-head">
              <h2 className="vl-h2" id="vl-journey">Learning journey</h2>
            </div>
            <Reveal as="ol" className="vl-steps" aria-label="Learning journey, in order">
              {journey.map((step, i) => (
                <li
                  key={step}
                  className="vl-step"
                  style={{ "--i": i, "--h": `${34 + i * 9}%`, "--w": `${46 + i * 8}%` }}
                >
                  <span className="vl-step-n">{String(i + 1).padStart(2, "0")}</span>
                  <span className="vl-step-t">{step}</span>
                </li>
              ))}
            </Reveal>
          </div>
        </section>

        {ENVIRONMENT_IMAGE && (
          <section className="vl-band" aria-label="VALMIKI learning environment">
            <div className="vl-wrap">
              <img src={ENVIRONMENT_IMAGE} alt={ENVIRONMENT_IMAGE_ALT} />
            </div>
          </section>
        )}

        {/* VISION + MISSION — two full-bleed panels */}
        <section className="vl-vm" aria-label="Vision and mission">
          <Reveal className="vl-vm-panel vl-vm-vision">
            <h2 className="vl-h2 vl-r" id="vl-vision" style={{ "--i": 0 }}>Vision</h2>
            <p className="vl-vision vl-r" style={{ "--i": 1 }}>
              To build an AI-ready generation capable of understanding, applying and adapting to
              emerging technologies responsibly and effectively.
            </p>
          </Reveal>
          <Reveal className="vl-vm-panel vl-vm-mission">
            <h2 className="vl-h2 vl-r" id="vl-mission" style={{ "--i": 0 }}>Mission</h2>
            <ul className="vl-mission" aria-labelledby="vl-mission">
              {mission.map((m, i) => (
                <li key={m} className="vl-r" style={{ "--i": i + 1 }}>{m}</li>
              ))}
            </ul>
          </Reveal>
        </section>

        {/* CLOSING */}
        <section className="vl-section vl-white" aria-labelledby="vl-close">
          <div className="vl-wrap vl-close-row">
            <h2 className="vl-h2" id="vl-close">Build AI capability with VALMIKI.</h2>
            <div className="vl-actions">
              <a className="vl-btn vl-btn-gold" href={VALMIKI_ROUTES.program}>Explore the Program</a>
              <a className="vl-btn vl-btn-line-ink" href={VALMIKI_ROUTES.partner}>Partner With Us</a>
            </div>
          </div>
        </section>
      </main>

      <ValmikiFooter />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}